import { createFileRoute, retainSearchParams, useParams, useNavigate, Link } from '@tanstack/react-router'
import type { RootSearch } from '@/routes/__root'
import { Suspense, useState, useDeferredValue } from 'react'
import { useSuspenseQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { usePermissions } from '@/hooks/use-permissions'
import { useLingui } from '@lingui/react/macro'
import { toast } from '@/lib/toast'
import { BriefcaseIcon, SearchIcon, PlusIcon, FileTextIcon } from 'lucide-react'
import { useSearchParamDialog } from '@/hooks/use-search-param-dialog'
import { useRootSearch } from '@/hooks/use-root-search'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { Button, ButtonLink } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { Command, CommandDialog, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command'
import { SectionLabel } from '@/components/custom/section-label'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { AnimatedOutlet } from './-components/animated-outlet'
import { ReportPager } from './-components/report/report-pager'
import { usePreviewMode } from '@/components/custom/route-transition/surface/route-preview'
import { NavStackStage, NavStackScreen, useStackedLayout } from '@/components/custom/route-transition/stack/nav-stack'
import { SectionStack } from './-components/shared/section-stack'
import { ReportScreenTitle } from './-components/report/report-screen-title'
import { ListPanelBodySkeleton, MembersPanelSkeleton } from './-components/app-layout-skeleton'
import { ListPanePending } from './-components/shared/list-pane-pending'
import {
  PageLayout,
  StickyPanel,
  StickyHeader,
  ScrollableContent,
  ScrollableArea,
  SlideInPanel,
  CollapsibleListPanel,
  CollapsibleListPanelBorder,
} from './-components/shared/layout'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
} from './-components/shared/collapsible-panel'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint, CollapsibleSectionLabel, CollapsibleToolbar } from './-components/shared/collapsible'
import { Avatar } from './-components/shared/avatar'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { ReportTabs } from './-components/report/report-tabs'
import { OfferTreeItem } from './-components/offers/offer-tree-item'
import { OffersApplicantsPanel } from './-components/offers/offers-applicants-panel'
import { OfferActionMenu } from './-components/offers/offer-action-menu'
import {
  ApplicantOffersPanelContent,
  useApplicantOffersInfinite,
} from './-components/applicants/applicant-offers-panel-content'
// Loader-facing factories come from the LIGHT query module — importing them through the heavy panel
// view dragged the whole view into the entry chunk (loaders load eagerly; see applicant-offers-query.ts).
import {
  applicantOffersInput,
  applicantOffersInfiniteQueryOptions,
} from './-components/applicants/applicant-offers-query'
import { companyOffersInput, companyOffersInfiniteQueryOptions } from './-components/companies/company-offers-query'
// Dialogs — only mount when triggered by user (search-param or selection state).
import { OfferDialog } from './-components/companies/offer-dialog'
import { DescribeProfileDialog } from './-components/offers/describe-profile-dialog'
import { EmployeeForm } from './-components/companies/employee-form'

const CRUMB_LINK_CLASS = 'text-foreground hover:text-foreground/70 text-base font-semibold sm:text-xl'

export const Route = createFileRoute('/_app/offers')({
  search: {
    middlewares: [retainSearchParams<RootSearch>(true)],
  },
  loaderDeps: ({ search }) => ({ q: search.q, o: search.o, type: search.type }),
  loader: ({ context, deps }) => {
    context.queryClient.prefetchInfiniteQuery(companyOffersInfiniteQueryOptions(companyOffersInput(deps, false)))
    context.queryClient.prefetchInfiniteQuery(applicantOffersInfiniteQueryOptions(applicantOffersInput(deps)))
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
})

function RouteComponent() {
  // Company users — and a promoted admin/authorized employee in company view (Block C) — get the
  // three-panel company layout; everyone else (applicants) gets the two-panel offers list + report.
  const { isCompanyView } = usePermissions()
  if (isCompanyView) {
    return <CompanyOffersView />
  }
  return <ApplicantOffersView />
}

function CompanyOffersView() {
  const { t } = useLingui()

  // Inside a report-tab live preview (/offers/<id>/board …), strip to just the pager pane.
  if (usePreviewMode('/offers') === 'passthrough') return <ReportPager className="h-full" />

  return (
    <div id="report" className="@container flex h-full flex-col">
      {/* On a phone the three panes below are STACKED SCREENS, not a squashed row: the offers list, the offer's
          candidates, and the report, each pushed in from the right over the last (see nav-stack.tsx).
          The provider sits above PageLayout so the title bar can carry the stack's back chevron. */}
      <OffersStack>
        <PageLayout
          icon={NAVIGATION_ICONS.offers}
          title={t`Offers`}
          breadcrumbsSlot={<OffersBreadcrumbs />}
          titleActions={<OffersTitleActions />}
        >
          <OffersLayout />
        </PageLayout>
      </OffersStack>
    </div>
  )
}

/**
 * Offers' mobile hierarchy — list → the offer's candidates → its report. The standard section shape, so the
 * whole declaration is the three routes involved; `SectionStack` owns the depth derivation and the pop policy.
 */
function OffersStack(props: { children: React.ReactNode }) {
  const { offerId } = useParams({ strict: false })
  return (
    <SectionStack list={{ to: '/offers' }} detail={offerId ? { to: '/offers/$offerId', params: { offerId } } : null}>
      {props.children}
    </SectionStack>
  )
}

function ApplicantOffersView() {
  const { t } = useLingui()
  return (
    <div id="report" className="@container flex h-full flex-col">
      <PageLayout icon={NAVIGATION_ICONS.offers} title={t`Offers`} breadcrumbsSlot={<OffersBreadcrumbs />}>
        <ApplicantOffersLayout />
      </PageLayout>
    </div>
  )
}

function OffersLayout() {
  const { t } = useLingui()
  const { offerId } = useParams({ strict: false })
  const navigate = useNavigate()
  const [describeDialogOpen, setDescribeDialogOpen] = useState(false)
  const [dialogOfferId, setDialogOfferId] = useState<number | null>(null)
  const [postCreateNavigateOffers, setPostCreateNavigateOffers] = useState(new Set<number>())

  const handleDialogClose = (open: boolean) => {
    setDescribeDialogOpen(open)
    if (!open && dialogOfferId) {
      const closedOfferId = dialogOfferId
      // Clear dialogOfferId after a small delay to allow dialog to close gracefully
      setTimeout(() => setDialogOfferId(null), 300)
      if (postCreateNavigateOffers.has(closedOfferId)) {
        setPostCreateNavigateOffers((prev) => {
          const next = new Set(prev)
          next.delete(closedOfferId)
          return next
        })
        toast.success(t`Saved successfully`)
        navigate({
          to: '/offers/$offerId/report',
          params: { offerId: closedOfferId.toString() },
        })
      }
    }
  }

  const hasOfferSelected = !!offerId

  const offerDialog = useSearchParamDialog('editOffer')
  const editOfferIdFromSearch = typeof offerDialog.value === 'number' ? offerDialog.value : undefined

  const employeeDialog = useSearchParamDialog('editEmployee')
  const editEmployeeId = typeof employeeDialog.value === 'number' ? employeeDialog.value : undefined

  // No viewport-driven auto-collapse any more: the nav stack gives each pane the WHOLE screen and decides
  // which one is on it, so both providers stay expanded and the collapse is desktop's business only
  // (where `isMobile` was false regardless — this is a no-op there).
  return (
    <CollapsiblePanelProvider defaultCollapsed={false}>
      <Suspense fallback={null}>
        {employeeDialog.mounted && (
          <EmployeeForm
            employeeId={editEmployeeId}
            open={employeeDialog.open}
            onOpenChange={employeeDialog.onOpenChange}
          />
        )}
        {dialogOfferId != null && (
          <DescribeProfileDialog jobId={dialogOfferId} open={describeDialogOpen} onOpenChange={handleDialogClose} />
        )}
        {offerDialog.mounted && (
          <OfferDialog
            offerId={editOfferIdFromSearch}
            open={offerDialog.open}
            onOpenChange={offerDialog.onOpenChange}
            onCreated={(newOfferId) => {
              setPostCreateNavigateOffers((prev) => new Set(prev).add(newOfferId))
              setDialogOfferId(newOfferId)
              setDescribeDialogOpen(true)
            }}
          />
        )}
      </Suspense>

      {/* The panel row on desktop, the stack's stage on mobile — same div, same classes, see
          NavStackStage. Each pane is wrapped in the level it occupies in the mobile hierarchy; on
          desktop those wrappers are `display: contents` and this lays out exactly as it always did. */}
      <NavStackStage className="flex w-full overflow-hidden" style={{ containerType: 'inline-size' }}>
        {/* LEVEL 0 — Offers list (always visible on desktop, squashes to a 52px icon column when an offer
            is selected; content keeps its full width inside and is clipped so only the avatars show) */}
        <NavStackScreen index={0} title={t`Offers`}>
          <CollapsibleListPanel isOpen={hasOfferSelected} className="print:hidden">
            <CollapsibleListPanelBorder>
              <CollapsiblePanel expandedWidth="100%" disableTransition>
                {/* Single-list panel: the list view inside `CollapsiblePanelContent` is a `@container`
                    child of `SquashPanel`, so the same rendered list responsively collapses into a
                    centered avatar column (icon rail) at narrow widths via container queries. */}
                <CollapsiblePanelContent>
                  <Suspense fallback={<ListPanelBodySkeleton />}>
                    <OffersPanelContent variant="sidebar" />
                  </Suspense>
                </CollapsiblePanelContent>
              </CollapsiblePanel>
            </CollapsibleListPanelBorder>
          </CollapsibleListPanel>
        </NavStackScreen>

        {/* Right Panel Group (Middle + Detail) - width animates from 0 to target. `ns-passthrough`: on
            mobile it stops being a box so the two levels inside position against the stage, not against
            this group's (0px / clipped) width. */}
        <SlideInPanel isOpen={hasOfferSelected} innerClassName="flex" className="ns-passthrough">
          {/* LEVEL 1 — the offer's candidates. The first screen an offer opens onto on mobile. */}
          <NavStackScreen
            index={1}
            title={<OfferScreenTitle />}
            actions={offerId ? <OfferScreenActions offerId={offerId} /> : null}
          >
            <CollapsiblePanelProvider cookieName="panel_state_middle" keyboardShortcut="]" defaultCollapsed={false}>
              <StickyPanel className="print:hidden">
                <CollapsiblePanel>
                  <Suspense fallback={<MembersPanelSkeleton />}>
                    <OffersApplicantsPanel />
                  </Suspense>
                </CollapsiblePanel>
              </StickyPanel>
            </CollapsiblePanelProvider>
          </NavStackScreen>

          {/* LEVEL 2 — the report. The `offers.$offerId` layout route renders here via the report pager.
              It lives HERE (not in the `$offerId` layout) so the enter animation plays once when the report
              panel first appears and never re-animates on tab/offer switches. */}
          <NavStackScreen index={2} title={<ReportScreenTitle />}>
            <ScrollableContent>
              <StickyHeader>
                <ReportTabs />
              </StickyHeader>
              {/* No <ScrollableArea/> wrapper: the pager puts one inside each pane (see ReportPager). */}
              {hasOfferSelected && <ReportPager className="min-h-0 flex-1" />}
            </ScrollableContent>
          </NavStackScreen>
        </SlideInPanel>
      </NavStackStage>
    </CollapsiblePanelProvider>
  )
}

/**
 * The title of the OFFER screen (nav stack level 1) — the offer's own name, the same thing the desktop
 * breadcrumb's second crumb shows. Suspends on `getJob`, which the `$offerId` layout has already prefetched,
 * so it fills in with the rest of the screen; the fallback keeps the bar's height either way.
 */
function OfferScreenTitle() {
  const { offerId } = useParams({ strict: false })
  if (!offerId) return null
  return (
    <Suspense fallback={null}>
      <OfferName offerId={offerId} />
    </Suspense>
  )
}

function OfferName(props: { offerId: string }) {
  const { data: offer } = useSuspenseQuery(trpc.jobs.getJob.queryOptions({ jobId: Number(props.offerId) }))
  return <>{offer?.name ?? ''}</>
}

/**
 * The OFFER screen's header actions: the offer's own report (one push deeper, the same level a tapped
 * candidate lands on but with the offer selected instead of a person — on desktop that pane is always beside
 * the list, so there is nothing to reach for there) and the owner's action menu.
 */
function OfferScreenActions(props: { offerId: string }) {
  const { t } = useLingui()
  return (
    <>
      <ButtonLink
        variant="ghost"
        size="icon"
        aria-label={t`Offer report`}
        className="size-8"
        to="/offers/$offerId/report"
        params={{ offerId: props.offerId }}
        search={(prev) => ({ ...prev, second: undefined, selected: 'first' })}
      >
        <FileTextIcon className="size-4" />
      </ButtonLink>
      <Suspense fallback={null}>
        <OffersTitleActionsLoaded offerId={props.offerId} />
      </Suspense>
    </>
  )
}

function OffersBreadcrumbs() {
  const { t } = useLingui()
  const { offerId } = useParams({ strict: false })
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            className={CRUMB_LINK_CLASS}
            render={
              <Link
                to="/offers"
                search={(prev) => ({ ...prev, first: undefined, second: undefined, selected: undefined })}
              >
                {t`Offers`}
              </Link>
            }
          />
        </BreadcrumbItem>
        {offerId && (
          <Suspense fallback={null}>
            <OfferDetailCrumb offerId={offerId} />
          </Suspense>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function OfferDetailCrumb(props: { offerId: string }) {
  const { data: offer } = useSuspenseQuery(trpc.jobs.getJob.queryOptions({ jobId: Number(props.offerId) }))
  if (!offer?.name) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/offers/$offerId/report"
              params={{ offerId: props.offerId }}
              search={(prev) => ({ ...prev, first: undefined, second: undefined, selected: undefined })}
            >
              {offer.name}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function OffersTitleActions() {
  const { offerId } = useParams({ strict: false })
  if (!offerId) return null
  return (
    <Suspense fallback={null}>
      <OffersTitleActionsLoaded offerId={offerId} />
    </Suspense>
  )
}

function OffersTitleActionsLoaded(props: { offerId: string }) {
  const { data: offer } = useSuspenseQuery(trpc.jobs.getJob.queryOptions({ jobId: Number(props.offerId) }))
  return offer.viewerRole === 'owner' ? <OfferActionMenu offer={offer} variant="header" /> : null
}

function ApplicantOffersLayout() {
  const { offerId } = useParams({ strict: false })
  const { first, second } = useRootSearch()
  const { query } = useApplicantOffersInfinite()

  const hasOfferSelected = !!offerId
  const hasOffers = query.data.pages.flatMap((page) => page.list).length > 0

  const hasProfileSelected =
    first?.type === 'job' || first?.type === 'applicant' || second?.type === 'job' || second?.type === 'applicant'

  return (
    <CollapsiblePanelProvider>
      <div className="flex h-full w-full">
        {/* When no offer is selected, show the offers list / insights dashboard in the main panel */}
        {!hasOfferSelected && (
          <ScrollableContent>
            <ScrollableArea>
              <ApplicantOffersPanelContent variant="main" />
            </ScrollableArea>
          </ScrollableContent>
        )}

        {/* Right Panel - Report (scrolls internally) */}
        {hasOfferSelected && (
          <ScrollableContent>
            <ScrollableArea>
              {hasProfileSelected ? (
                <AnimatedOutlet className="h-full" />
              ) : hasOffers ? (
                <ApplicantOffersEmptyState type="select-offer" />
              ) : (
                <ApplicantOffersEmptyState type="no-offers" />
              )}
            </ScrollableArea>
          </ScrollableContent>
        )}
      </div>
    </CollapsiblePanelProvider>
  )
}

// Reads the loader-prefetched `getOffersInfinite` (see this route's `loader`) and renders the
// company offers rail list.
function OffersPanelContent(props: { variant?: 'sidebar' | 'main' }) {
  const { t } = useLingui()
  const [searchOpen, setSearchOpen] = useState(false)
  const { offerId } = useParams({ strict: false })
  const { q: searchQueryFromUrl } = useRootSearch()
  const navigate = useNavigate()
  // In a stack this list is a whole level, and a pop always lands on it with the selection cleared — so its
  // hint must be on screen ALREADY while the level above is dragged off it, not appear on the commit and
  // shove the list down a row at the end of the gesture. (The rows' own selected highlight is suppressed
  // inside OfferTreeItem — see useStackedSelection.)
  const stacked = useStackedLayout()

  const handleCreateOffer = () => {
    navigate({ to: '.', search: (prev) => ({ ...prev, editOffer: 'new' }) })
  }
  // Create is owner-only (company account, or admin in company view); an `authorized` employee viewing
  // the company is read-only (the backend also rejects the write — Block A/C).
  const canCreateOffers = usePermissions().can('jobs:write')

  const { searchInput, handleSearchChange } = useDebouncedSearch({
    initialValue: searchQueryFromUrl,
  })

  const offersInput = companyOffersInput({ q: searchQueryFromUrl }, false)
  const deferredOffersInput = useDeferredValue(offersInput)
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(
    companyOffersInfiniteQueryOptions(deferredOffersInput),
  )
  // True while the deferred input lags the live one (a search is in flight) — keeps the search spinner
  // / fetching bar visible during the keep-previous transition.
  const isOffersFetching = isFetching || deferredOffersInput !== offersInput

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  })

  // Flatten all pages
  const allOffers = data?.pages.flatMap((page) => page.list) ?? []

  // Get total count from first page
  const totalCount = data?.pages[0]?.pagination.count ?? 0
  // The search term that produced the list currently on screen (the DEFERRED `q`, not the live URL
  // one). Empty-state decisions key off this so clearing the search jumps straight to the original
  // list instead of flashing the cold "No offers yet" state while the stale search list is still shown.
  const activeQuery = deferredOffersInput.q
  const showOnlyEmptyState = totalCount === 0 && !activeQuery

  const isMainVariant = props.variant === 'main'

  if (isMainVariant) {
    // Main panel variant - full UI adapted for main content area
    return (
      <div className="flex h-full flex-col">
        {!showOnlyEmptyState && (
          <>
            {/* Search bar with add button */}
            <div className="flex items-center gap-2 border-b p-3">
              <SearchInput
                placeholder={t`Search offers`}
                value={searchInput}
                onChange={handleSearchChange}
                isLoading={isOffersFetching && searchQueryFromUrl.length > 0}
              />
              {canCreateOffers && (
                <Button variant="secondary" onClick={handleCreateOffer}>
                  <PlusIcon className="size-4" />
                  {t`Add`}
                </Button>
              )}
            </div>
          </>
        )}

        {/* Offers list */}
        <ScrollableList
          isFetching={isOffersFetching}
          loadMoreRef={loadMoreRef}
          isFetchingMore={isFetchingNextPage}
          isEmpty={allOffers.length === 0}
          emptyState={
            <PanelEmptyState
              noResults={allOffers.length === 0 && !!activeQuery}
              icon={<NAVIGATION_ICONS.offers className="text-muted-foreground size-5" />}
              title={t`No offers yet`}
              description={t`Add offers to get started`}
              action={
                canCreateOffers ? (
                  <Button variant="secondary" onClick={handleCreateOffer}>
                    <PlusIcon className="size-4" />
                    {t`Add offer`}
                  </Button>
                ) : undefined
              }
            />
          }
          hint={
            allOffers.length > 0 && (
              <SelectionHint
                visible={stacked || !offerId}
                text={t`Select an offer to view its candidates and reports`}
              />
            )
          }
        >
          <SectionLabel className="px-2">
            {totalCount === 1 ? t`${totalCount} offer` : t`${totalCount} offers`}
          </SectionLabel>
          {allOffers.map((offer) => (
            <OfferTreeItem key={offer.id} offer={offer} isSelected={offerId === offer.id.toString()} />
          ))}
        </ScrollableList>
      </div>
    )
  }

  return (
    <>
      {!showOnlyEmptyState && (
        <>
          <CollapsibleToolbar>
            <CollapsibleToolbar.Search onSearchClick={() => setSearchOpen(true)} searchLabel={t`Search offers`}>
              <SearchInput
                placeholder={t`Search offers`}
                value={searchInput}
                onChange={handleSearchChange}
                isLoading={isOffersFetching && searchQueryFromUrl.length > 0}
              />
            </CollapsibleToolbar.Search>
            {canCreateOffers && (
              <CollapsibleToolbar.Action
                icon={<PlusIcon className="size-4" />}
                label={t`Add`}
                tooltip={t`Add offer`}
                onClick={handleCreateOffer}
              />
            )}
          </CollapsibleToolbar>
        </>
      )}

      <ScrollableList
        isFetching={isOffersFetching}
        loadMoreRef={loadMoreRef}
        isFetchingMore={isFetchingNextPage}
        isEmpty={allOffers.length === 0}
        hint={
          allOffers.length > 0 && (
            <SelectionHint visible={stacked || !offerId} text={t`Select an offer to view its candidates and reports`} />
          )
        }
        emptyState={
          <PanelEmptyState
            noResults={allOffers.length === 0 && !!activeQuery}
            icon={<NAVIGATION_ICONS.offers className="text-muted-foreground size-5" />}
            title={t`No offers yet`}
            description={t`Add offers to get started`}
            action={
              <Button variant="secondary" onClick={handleCreateOffer}>
                <PlusIcon className="size-4" />
                {t`Add offer`}
              </Button>
            }
          />
        }
      >
        <CollapsibleSectionLabel>
          {totalCount === 1 ? t`${totalCount} offer` : t`${totalCount} offers`}
        </CollapsibleSectionLabel>
        {allOffers.map((offer) => (
          <OfferTreeItem key={offer.id} offer={offer} isSelected={offerId === offer.id.toString()} />
        ))}
      </ScrollableList>

      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen} title={t`Search offers`}>
        <Command className="gap-1">
          <CommandInput placeholder={t`Search offers`} />
          <CommandList>
            <CommandEmpty>{t`No offers found`}</CommandEmpty>
            {allOffers.map((offer) => (
              <CommandItem
                key={offer.id}
                value={`${offer.name ?? ''} ${offer.id}`}
                onSelect={() => {
                  navigate({
                    to: '/offers/$offerId/report',
                    params: { offerId: offer.id.toString() },
                    search: (prev) => ({
                      ...prev,
                      first: { type: 'job', id: offer.id },
                      second: undefined,
                      selected: 'first',
                    }),
                  })
                  setSearchOpen(false)
                }}
              >
                <Avatar size="sm" type="job" name={offer.name} />
                <span className="truncate">{offer.name}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  )
}

function ApplicantOffersEmptyState(props: { type: 'no-offers' | 'select-offer' }) {
  const { t } = useLingui()

  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-4 px-8 text-center">
      <div className="bg-muted rounded-full p-4">
        {props.type === 'no-offers' ? (
          <BriefcaseIcon className="text-muted-foreground size-8" />
        ) : (
          <SearchIcon className="text-muted-foreground size-8" />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-medium">{props.type === 'no-offers' ? t`No offers found` : t`Select an offer`}</h3>
        {props.type === 'no-offers' ? (
          <p className="text-muted-foreground max-w-[480px] text-sm">
            {t`Search for offers to compare your profile and find the best opportunities for you.`}
          </p>
        ) : (
          <div className="flex items-center gap-1.5">
            <SearchIcon className="text-muted-foreground/70 size-3.5" />
            <p className="text-muted-foreground/70 text-xs">{t`Select an offer to view their report`}</p>
          </div>
        )}
      </div>
    </div>
  )
}
